import { z } from 'zod'

// https://docs.elationhealth.com/reference/the-report-object

export const REPORT_STATUS = [
  'CORRECTED',
  'DELETED',
  'FINAL',
  'PENDING',
  'PRELIMINARY',
  'RESULTS ENTERED -- NOT VERIFIED',
  'PARTIAL',
  'RESULTS STATUS CHANGE TO FINAL. RESULTS DID NOT CHANGE ( DONT TRANSMIT TEST).',
  'RESULT CANCELED DUE TO NON-PERFORMANCE',
  'ERROR',
  'AMENDED',
] as const

export const REPORT_TYPES = [
  'Lab',
  'Cardiac',
  'Imaging',
  'Hospital',
  'Consultation',
  'Legal',
  'Misc',
  'PatientForm',
] as const

export const ABNORMAL_FLAGS = [
  'Below low normal',
  'Above high normal',
  'Below lower panic limits',
  'Above upper panic limits',
  'Below absolute low-off instrument scale',
  'Above absolute high-off instrument scale',
  'Normal',
  'Abnormal',
  'Very abnormal',
  'Positive result',
  'Negative result',
  'Intermediate result',
  'Significant change up',
  'Significant change down',
  'Better',
  'Worse',
  'Susceptible',
  'Resistant',
  'Intermediate',
  'Moderately susceptible',
  'Very susceptible',
  'Critical',
  'Beta-lactamase Pos',
  'Not Applicable',
  'Very High',
  'Negative',
] as const

export const labTestSchema = z.object({
  code: z.string(),
  name: z.string(),
  loinc: z.string().optional().nullable(),
})

export type LabTest = z.infer<typeof labTestSchema>

export const labTestCategorySchema = z.object({
  value: z.string(),
  description: z.string(),
})

export type LabTestCategory = z.infer<typeof labTestCategorySchema>

export const labResultSchema = z.object({
  status: z.enum(REPORT_STATUS),
  value: z.string().nullable(),
  value_type: z.string().nullable(),
  text: z.string().nullable(),
  note: z.string(),
  reference_min: z.string().optional(),
  reference_max: z.string().optional(),
  units: z.string().optional(),
  is_abnormal: z.boolean(),
  abnormal_flag: z.string().optional().nullable(),
  test: labTestSchema,
  test_category: labTestCategorySchema,
})

export type LabResult = z.infer<typeof labResultSchema>

export const labGridSchema = z.object({
  accession_number: z.string(),
  resulted_date: z.string(),
  collected_date: z.string(),
  status: z.enum(REPORT_STATUS),
  note: z.string(),
  results: z.array(labResultSchema),
})

export type LabGrid = z.infer<typeof labGridSchema>

export const reportImageSchema = z.object({
  document: z.string().describe('url of the document'),
})

export type ReportImage = z.infer<typeof reportImageSchema>

export const EXTENDED_REPORT_TYPES = [
  ...REPORT_TYPES,
  'Allergist',
  'Audiology',
  'Billing',
  'Business',
  'Forms',
  'Correspondence',
  'Cosmetic',
  'Diabetes',
  'Directives',
  'DME',
  'EKG',
  'Endocrinology',
  'Exams',
  'EyeVision',
  'GI',
  'History',
  'Home',
  'Hospice',
  'Incoming',
  'Injection',
  'Insurance',
  'Intake',
  'Neuro',
  'Nursing',
  'OBGYN',
  'OT',
  'OldRecord',
  'Oncology',
  'Ophth',
  'Ortho',
  'ENT',
  'Outgoing',
  'Pathology',
  'Consent',
  'Pediatric',
  'Pharmacy',
  'PT',
  'Preop',
  'PaperRx',
  'PriorAuth',
  'Procedures',
  'Psych',
  'Pulmonary',
  'Quality',
  'Radiology',
  'Screening',
  'SNF',
  'Sleep',
  'Surgery',
  'Testing',
  'Therapy',
  'Ultrasound',
  'UrgentCare',
  'Urology',
  'Women',
] as const

export const FILE_CONTENT_TYPES = {
  EXTERNAL_CONTENT: 'application/json; schema=el8_external_content_report',
  BINARY: 'application/octet-stream',
} as const

export const externalContentFileSchema = z.object({
  content_type: z.literal(FILE_CONTENT_TYPES.EXTERNAL_CONTENT),
  external_content: z.object({
    referenced_url: z.string().url(),
    summary: z.string(),
  }),
})

export const base64ContentFileSchema = z.object({
  content_type: z
    .string()
    .refine(
      (val) => val !== FILE_CONTENT_TYPES.EXTERNAL_CONTENT,
      'Content type cannot be external content type'
    ),
  external_content: z.unknown().nullable().optional(),
  original_filename: z.string().nullable().optional(),
  base64_content: z.string(),
})

export const reportFileSchema = z.union([
  externalContentFileSchema,
  base64ContentFileSchema,
])

export type ExternalContentFile = z.infer<typeof externalContentFileSchema>
export type Base64ContentFile = z.infer<typeof base64ContentFileSchema>
export type ReportFile = z.infer<typeof reportFileSchema>

export const reportSchema = z.object({
  id: z.number(),
  patient: z.number(),
  report_type: z.enum(REPORT_TYPES),
  requisition_number: z.string().nullable(),
  reported_date: z.string().nullable(),
  grids: z.array(labGridSchema),
  custom_title: z.string().nullable(),
  tags: z.array(z.unknown()),
  images: z.array(reportImageSchema),
  files: z.array(reportFileSchema),
  practice: z.number(),
  physician: z.number(),
  document_date: z.string(),
  chart_date: z.string(),
  signed_date: z.string().nullable(),
  signed_by: z.number().nullable(),
  created_date: z.string(),
  vendor: z.number().nullable(),
  printable_view: z.string(),
  deleted_date: z.string().nullable(),
})

export type Report = z.infer<typeof reportSchema>

export const externalReportSchema = z.object({
  id: z.number(),
  patient: z.number(),
  custom_title: z.string().nullable(),
  report_type: z.enum(EXTENDED_REPORT_TYPES),
  images: z.array(reportImageSchema).optional(),
  files: z.array(reportFileSchema),
  practice: z.number(),
  physician: z.number().optional().nullable(),
  document_date: z.string(),
  chart_date: z.string(),
  signed_date: z.string().nullable(),
  signed_by: z.number().nullable().optional(),
  created_date: z.string(),
  deleted_date: z.string().nullable(),
})

export type ExternalReport = z.infer<typeof externalReportSchema>

export function extractFilenameFromContentType(
  contentType: string
): string | null {
  const match = contentType.match(/name="([^"]+)"/)
  return match ? match[1] : null
}
